import React, { useEffect, useState } from "react";
import { getResourceCost } from "../../../services/graphsService";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  AreaSeries,
  DateTime,
  Tooltip,
  Legend,
} from "@syncfusion/ej2-react-charts";

const TotalResourceCost = ({ skills, selectedSkill, selectedRange }) => {
  const [range, setRange] = useState("");
  const [skill, setSkill] = useState("");
  const [totalCostData, setTotalCostData] = useState([]);
  const [loading, setLoading] = useState(false);

  const extractDays = (str) => {
    const match = str.match(/\d+/);
    return match ? Number(match[0]) : null;
  };

  useEffect(() => {
    if (selectedSkill) setSkill(selectedSkill);
    if (selectedRange) setRange(selectedRange);
  }, [selectedRange, selectedSkill]);

  // ✅ Transform API data into a single cumulative dataset
  const transformDataForChart = (apiData) => {
    const grouped = {};

    apiData.forEach((item) => {
      const dateKey = new Date(item.TimeStamp).toISOString().split("T")[0];
      grouped[dateKey] = (grouped[dateKey] || 0) + item.TotalCost;
    });

    // Convert to array of points and sort by date
    let cumulativeSum = 0;
    const finalData = Object.keys(grouped)
      .sort((a, b) => new Date(a) - new Date(b))
      .map((date) => {
        cumulativeSum += grouped[date];
        return {
          x: new Date(date),
          y: parseFloat(cumulativeSum.toFixed(2)),
        };
      });

    return finalData;
  };

  useEffect(() => {
    const fetchResourceCostData = async () => {
      try {
        const newRange = extractDays(range);
        setLoading(true);

        const res = await getResourceCost(parseInt(skill), parseInt(newRange));
        if (res) {
          const transformed = transformDataForChart(res);
          setTotalCostData(transformed);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (range && skill && skill !== "-1") {
      fetchResourceCostData();
    } else {
      setTotalCostData([]);
    }
  }, [range, skill]);

  // ✅ Final total cost = last cumulative value
  const overallTotal =
    totalCostData.length > 0 ? totalCostData[totalCostData.length - 1].y : 0;

  return (
    <div className="chart-display-width">
      <h3>Cumulative Resource Cost per Skill</h3>
      {loading ? (
        <div className="loader-map">
          <div className="loader-wrap">
            <div className="loader-dot loader-dot1"></div>
            <div className="loader-dot loader-dot2"></div>
            <div className="loader-dot loader-dot3"></div>
            <div className="loader-dot loader-dot4"></div>
            <div className="loader-text loader-map-text-display ">
              Loading Resource Cost...
            </div>
          </div>
        </div>
      ) : !totalCostData || totalCostData.length === 0 ? (
        <div className="container-no-records">No Records Found</div>
      ) : (
        <>
          <ChartComponent
            primaryXAxis={{ valueType: "DateTime", labelFormat: "MMM dd" }}
            primaryYAxis={{
              title: "Cumulative Cost (USD)",
              minimum: 0,
              labelFormat: "${value}",
              //labelFormat: "${value:n2}", // ✅ axis with 2 decimals
            }}
            tooltip={{
              enable: true,
              // format: "${point.x} : $${point.y}", // ✅ will use y as 2 decimals
            }}
            legendSettings={{ visible: false }}
          >
            <Inject services={[AreaSeries, DateTime, Tooltip, Legend]} />
            <SeriesCollectionDirective>
              <SeriesDirective
                dataSource={totalCostData}
                xName="x"
                yName="y"
                name="Cumulative Cost"
                type="Area"
                width={2}
                marker={{ visible: true }}
                opacity={0.7}
              />
            </SeriesCollectionDirective>
          </ChartComponent>

          {/* ✅ Custom summary below chart */}
          <div className="chart-text-display">
            {skills.find((s) => s.Id === parseInt(selectedSkill))?.Name ||
              "Selected Skill"}{" "}
            : ${overallTotal.toFixed(2)}
          </div>
        </>
      )}
    </div>
  );
};

export default TotalResourceCost;
